import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import CustomLegend from "../../../components/reusable/CustomLegend";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import { getEngagementsBaseUrl } from "src/config/endpoints";

const ENGAGEMENTS_BASE_URL = getEngagementsBaseUrl();

interface CampaignInfo {
  id: string;
  name: string;
}

interface CampaignStatsData {
  campaign_name: string;
  sendgrid_campaign_id: string;
  total_sent: number;
  delivered: number;
  bounces: number;
  opens: number;
  unique_opens: number;
  clicks: number;
  unique_clicks: number;
  unsubscribes: number;
  open_rate: number;
  click_rate: number;
  unsubscribe_rate: number;
  conversion_rate: number;
  email_forwarding_rate: number;
  new_users: number;
  returning_users: number;
  bounce_rate: number;
  click_through_rate: number;
}

interface CampaignStatsResponse {
  campaigns: CampaignStatsData[];
  total_emails: number;
}

interface CampaignData {
  name: string;
  Delivered: number;
  "Open Rate": number;
  CTR: number;
  "Bounce Rate": number;
  "Conversion Rate": number;
  "Unsubscribe Rate": number;
  "Share Rate": number;
}

const CampaignComparison: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const baseCampaign = (location.state?.baseCampaign ?? null) as CampaignInfo | null;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CampaignData[]>([]);
  const [campaigns, setCampaigns] = useState({
    base: baseCampaign?.name ?? "",
    compareWith: "",
    compareWithSecond: "",
  });
  const [showThirdComparison, setShowThirdComparison] = useState(false);
  const [availableCampaigns, setAvailableCampaigns] = useState<CampaignInfo[]>(
    []
  );

  useEffect(() => {
    const storedCampaigns = localStorage.getItem("campaignInfo");
    if (storedCampaigns) {
      try {
        const campaignsList = JSON.parse(storedCampaigns) as CampaignInfo[];
        setAvailableCampaigns(
          Array.isArray(campaignsList)
            ? campaignsList.filter(
                (campaign): campaign is CampaignInfo =>
                  Boolean(campaign?.id && campaign?.name)
              )
            : []
        );
      } catch {
        setAvailableCampaigns([]);
      }
    }
  }, []);

  useEffect(() => {
    if (
      baseCampaign?.id &&
      baseCampaign?.name &&
      availableCampaigns.length > 0
    ) {
      // Set the base campaign in dropdown immediately
      setCampaigns(prev => ({ ...prev, base: baseCampaign.name }));

      const fetchAndSet = async () => {
        const stats = await fetchCampaignStats(
          baseCampaign.id,
          baseCampaign.name
        );
        if (stats) {
          setData(prev => [stats, ...prev]);
        }
      };

      fetchAndSet();
    }
  }, [baseCampaign, availableCampaigns]);

  const fetchCampaignStats = async (
    campaignId: string,
    campaignName: string
  ) => {
    try {
      const tenantIdStr = localStorage.getItem("tenant_id");
      const apiKey = localStorage.getItem("api_key");

      if (!apiKey || !tenantIdStr) {
        console.error("Missing api_key or tenant_id in localStorage");
        return null;
      }

      const tenantId = parseInt(tenantIdStr, 10);

      const response = await fetch(`${ENGAGEMENTS_BASE_URL}/campaigns-stats`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          tenant_id: tenantId,
          campaign_id: campaignId,
          campaign_name: campaignName,
        }),
      });

      const data: CampaignStatsResponse = await response.json();

      if (data.campaigns && data.campaigns.length > 0) {
        const stats = data.campaigns[0];
        return {
          name: stats.campaign_name,
          Delivered: stats.delivered,
          "Open Rate": stats.open_rate,
          CTR: stats.click_through_rate,
          "Bounce Rate": stats.bounce_rate,
          "Conversion Rate": Math.round(
            (stats.conversion_rate * stats.total_sent) / 100
          ),
          "Unsubscribe Rate": stats.unsubscribes,
          "Share Rate": Math.round(
            (stats.email_forwarding_rate * stats.total_sent) / 100
          ),
        };
      }

      return null;
    } catch (error) {
      console.error("Error fetching campaign stats:", error);
      return null;
    }
  };

  const handleCompare = async () => {
    if (!campaigns.base || !campaigns.compareWith) {
      return;
    }

    setLoading(true);
    try {
      const baseCampaignInfo = availableCampaigns.find(
        c => c.name === campaigns.base
      );
      const compareCampaignInfo = availableCampaigns.find(
        c => c.name === campaigns.compareWith
      );
      const compareSecondCampaignInfo = campaigns.compareWithSecond
        ? availableCampaigns.find(c => c.name === campaigns.compareWithSecond)
        : null;

      if (!baseCampaignInfo || !compareCampaignInfo) {
        console.error("Campaign info not found");
        return;
      }

      const campaignsToFetch = [
        fetchCampaignStats(baseCampaignInfo.id, baseCampaignInfo.name),
        fetchCampaignStats(compareCampaignInfo.id, compareCampaignInfo.name),
      ];

      if (compareSecondCampaignInfo) {
        campaignsToFetch.push(
          fetchCampaignStats(
            compareSecondCampaignInfo.id,
            compareSecondCampaignInfo.name
          )
        );
      }

      const results = await Promise.all(campaignsToFetch);
      const validResults = results.filter(
        (result): result is CampaignData => result !== null
      );

      if (validResults.length > 0) {
        setData(validResults);
      }
    } catch (error) {
      console.error("Error comparing campaigns:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMore = () => {
    setShowThirdComparison(true);
  };

  const getAvailableCampaignsForDropdown = (excludeNames: string[]) => {
    return availableCampaigns
      .filter(campaign => campaign?.name && !excludeNames.includes(campaign.name))
      .map(campaign => campaign.name);
  };

  const compareWithOptions = getAvailableCampaignsForDropdown([campaigns.base]);

  const compareWithSecondOptions = getAvailableCampaignsForDropdown([
    campaigns.base,
    campaigns.compareWith,
  ]);

  const handleRemoveCampaign = (campaignName: string) => {
    setCampaigns(prev => {
      const updated = { ...prev };
      if (updated.compareWith === campaignName) {
        updated.compareWith = "";
      }
      if (updated.compareWithSecond === campaignName) {
        updated.compareWithSecond = "";
        setShowThirdComparison(false);
      }
      return updated;
    });

    setData(prevData => prevData.filter(c => c?.name !== campaignName));
  };

  return (
    <div className="campaign-comparison">
      <button
        className="button button--primary "
        style={{ marginBottom: "20px" }}
        onClick={() => navigate("/account/engagement/campaign-analytics")}
      >
        <span>&lt;&nbsp;</span> {t("campaignFlow.compareCampaigns.goBack")}
      </button>

      <span
        className="headline"
        style={{ marginLeft: -1, marginBottom: "-130px" }}
      >
        {t("campaignFlow.compareCampaigns.title")}
      </span>
<div className="comparison-controls">
  <div className="campaign-selector">
    <label>{t("campaignFlow.compareCampaigns.baseCampaign")}</label>
    <StyledDropdown
      dropdownType="select"
      placeholder={t("campaignFlow.compareCampaigns.selectCampaign")}
      value={campaigns.base || undefined}
      onChange={value =>
        setCampaigns({ ...campaigns, base: value as string })
      }
      options={availableCampaigns
        .filter(campaign => Boolean(campaign?.name))
        .map(campaign => ({
          value: campaign.name,
          label: campaign.name,
        }))}
      className="custom-dropdown"
      popupClassName="custom-dropdown-menu"
      style={{height: "40px"}}
    />
  </div>

  <div className="campaign-selector">
    <label>{t("campaignFlow.compareCampaigns.compareWith")}</label>
    <StyledDropdown
      dropdownType="select"
      placeholder={t("campaignFlow.compareCampaigns.selectCampaign")}
      value={campaigns.compareWith || undefined}
      onChange={value =>
        setCampaigns({ ...campaigns, compareWith: value as string })
      }
      options={compareWithOptions.map(name => ({
        value: name,
        label: name,
      }))}
      disabled={!campaigns.base}
      className="custom-dropdown"
      popupClassName="custom-dropdown-menu"
      style={{height: "40px"}}
    />
  </div>

  {showThirdComparison && (
    <div className="campaign-selector">
      <label>{t("campaignFlow.compareCampaigns.compareWith")}:</label>
      <StyledDropdown
        dropdownType="select"
        placeholder={t("campaignFlow.compareCampaigns.selectCampaign")}
        value={campaigns.compareWithSecond || undefined}
        onChange={value =>
          setCampaigns({ ...campaigns, compareWithSecond: value as string })
        }
        options={compareWithSecondOptions.map(name => ({
          value: name,
          label: name,
        }))}
        disabled={!campaigns.compareWith}
        className="custom-dropdown"
        popupClassName="custom-dropdown-menu"
        style={{height: "40px"}}
      />
    </div>
  )}  

        <div className="button-group">
          {!showThirdComparison && (
            <button
              className="button button--outline "
              onClick={handleAddMore}
              disabled={!campaigns.compareWith}
              style={{ marginBottom: "0px" }}
            >
              {t("campaignFlow.compareCampaigns.addMore")}
            </button>
          )}
          <button
            className={`button button--primary ${
              !campaigns.base || !campaigns.compareWith || loading
                ? "button--disabled"
                : ""
            }`}
            onClick={handleCompare}
            disabled={!campaigns.base || !campaigns.compareWith || loading}
            style={{ marginBottom: "0px" }} // optional: adjust spacing
          >
            {loading
              ? t("campaignFlow.compareCampaigns.comparing")
              : t("campaignFlow.compareCampaigns.compare")}
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        data.length > 0 && (
          <div className="chart-container">
            <h3>{t("campaignFlow.compareCampaigns.chartTitle")}</h3>
            <div className="campaign-headers">
              {data.map(campaign => (
                <div className="campaign-header" key={campaign?.name ?? "campaign"}>
                  <span>{campaign?.name}</span>
                  {campaign?.name !== campaigns.base && campaign?.name && (
                    <button
                      onClick={() => handleRemoveCampaign(campaign.name)}
                      title={t("campaignFlow.compareCampaigns.removeThisCampaign")}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                      }}
                    >
                      <img
                        src="/Icons/cross-icon.svg"
                        alt={t("campaignFlow.compareCampaigns.remove")}
                        style={{ width: 20, height: 20, display: "block" }}
                      />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={data}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={false} />
                <YAxis />
                <Tooltip />
                <Legend content={<CustomLegend />} />
                <Bar dataKey="Delivered" fill="#03045E" />
                <Bar dataKey="Open Rate" fill="#023E8A" />
                <Bar dataKey="CTR" fill="#0077B6" />
                <Bar dataKey="Bounce Rate" fill="#00B4D8" />
                <Bar dataKey="Conversion Rate" fill="#FF9100" />
                <Bar dataKey="Unsubscribe Rate" fill="#FF6D00" />
                <Bar dataKey="Share Rate" fill="#A7B8D8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )
      )}
    </div>
  );
};

export default CampaignComparison;
